import { EditingCategory, ProductKind, ProductKindsEnergyCertificate, ProductKindsWithDocumentGallery, SELF_UPLOAD_PRODUCT_IDS } from 'constants/product'
import { FC, ReactNode, createContext, useContext, useMemo } from 'react'

import { ProductDTO } from 'models/product'
import { VisualFileType } from 'constants/visual'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryVisualType } from './GalleryVisualType.context'

interface GalleryProductInterface {
  product?: ProductDTO
  isVideo: boolean
  isVirtualVisit: boolean
  isFloorPlan: boolean
  isDroneProduct: boolean
  isDocumentProduct: boolean
  isVirtualVisitMatterport: boolean
  isVirtualVisitFloorfy: boolean
  isEnergyCertificateProduct: boolean
  isFloorPlanCertificationProduct: boolean
  isAuthoritiesDocumentsProduct: boolean
  isAIEditingProduct: boolean
  isSelfUploadProduct: boolean
}

const defaultGalleryProductValue: GalleryProductInterface = {
  isVideo: false,
  isVirtualVisit: false,
  isFloorPlan: false,
  isDroneProduct: false,
  isDocumentProduct: false,
  isVirtualVisitMatterport: false,
  isVirtualVisitFloorfy: false,
  isEnergyCertificateProduct: false,
  isFloorPlanCertificationProduct: false,
  isAuthoritiesDocumentsProduct: false,
  isAIEditingProduct: false,
  isSelfUploadProduct: false,
}

/** Gallery product context */
export const GalleryProductContext = createContext<GalleryProductInterface>(defaultGalleryProductValue)
/** Gallery product context hook */
export const useGalleryProduct = (): GalleryProductInterface => useContext(GalleryProductContext)

/** Context provider for gallery product */
export const GalleryProductContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  children,
}) => {
    const { product } = useGalleryAssignment()
    const { normalizedOriginalType } = useGalleryVisualType()

    const isVideo = useMemo(() => !!product?.isVideo, [product])

    // Virtual visit also known as virtual tour matterport, of kind HD
    const isVirtualVisitHD = useMemo(() => (
      (!!product?.isVirtualVisit && product?.kind === ProductKind.HD)
    ), [product])

    // Product type is kind SD
    const isVirtualVisitSD = useMemo(() => (
      (product?.kind === ProductKind.SD)
    ), [product])

    // 3D tour made with Floorfy: delivered as a share link, not a Matterport tour and not hosted by us
    const isVirtualVisitFloorfy = useMemo(() => (
      (product?.kind === ProductKind.FLOORFY_VIRTUAL_TOUR)
    ), [product])

    const isVirtualVisit = useMemo(() => {
      if (isVirtualVisitHD || isVirtualVisitSD || isVirtualVisitFloorfy) return true

      if (product?.kind && ProductKindsWithDocumentGallery.has(product.kind)) {
        return false
      }

      if (product?.kind === ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT) {
        return normalizedOriginalType === VisualFileType.RAW
      }

      if (product?.isFloorPlan && (product?.kind !== ProductKind.MOBILE_APPLICATION_FLOOR_PLAN && product?.category !== EditingCategory.EDITING)) {
        return normalizedOriginalType === VisualFileType.RAW
      }

      return false
    }, [isVirtualVisitHD, isVirtualVisitSD, isVirtualVisitFloorfy, product?.isFloorPlan, product?.category, product?.kind, normalizedOriginalType])

    const isVirtualVisitMatterport = useMemo(() => isVirtualVisit && (isVirtualVisitHD || isVirtualVisitSD), [isVirtualVisit, isVirtualVisitHD, isVirtualVisitSD])

    // The backend flag is a broader "is a floor plan" notion than this gallery wants: it also covers
    // document products carrying the legacy FLOOR_PLAN type (FLOOR_PLAN_CERTIFICATION,
    // MEASUREMENT_ON_SITE — ~2976 prod assignments), which render as document galleries, not floor
    // plans. Keep excluding them here. ISSUE-2539 follow-up: fold this into the backend flag.
    const isFloorPlan = useMemo(() => !!product?.isFloorPlan && !ProductKindsWithDocumentGallery.has(product.kind), [product])

    const isDroneProduct = useMemo(() => !!product && (product.kind === ProductKind.DRONE_PHOTO || product.kind === ProductKind.STANDARD_DRONE_VIDEO), [product])

    const isDocumentProduct = useMemo(() => !!product && ProductKindsWithDocumentGallery.has(product.kind), [product])

    // Also known as living area calculation
    const isFloorPlanCertificationProduct = useMemo(() => !!product && product.kind === ProductKind.FLOOR_PLAN_CERTIFICATION, [product])

    const isEnergyCertificateProduct = useMemo(() => !!product && ProductKindsEnergyCertificate.has(product.kind), [product])
    const isAuthoritiesDocumentsProduct = useMemo(() => !!product && product.kind === ProductKind.AUTHORITIES_DOCUMENTS, [product])
    const isAIEditingProduct = useMemo(() => !!product?.isAiPhotoEditing, [product])

    const isSelfUploadProduct = useMemo(() => !!product && !!product.id && SELF_UPLOAD_PRODUCT_IDS.has(product.id), [product])

    return (
      <GalleryProductContext.Provider
        value={{
          product,
          isVideo,
          isVirtualVisit,
          isFloorPlan,
          isDroneProduct,
          isDocumentProduct,
          isVirtualVisitMatterport,
          isVirtualVisitFloorfy,
          isEnergyCertificateProduct,
          isFloorPlanCertificationProduct,
          isAuthoritiesDocumentsProduct,
          isAIEditingProduct,
          isSelfUploadProduct,
        }}
      >
        {children}
      </GalleryProductContext.Provider>
    )
  }
